export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a logical formula for routing rules
 * @param formula - The formula string (e.g., "(A AND B) OR C")
 * @param validLetters - Array of valid condition letters
 * @returns ValidationResult with isValid flag and error messages
 */
export function validateFormula(formula: string, validLetters: string[]): ValidationResult {
  const errors: string[] = [];

  // Check if formula is empty
  if (!formula || formula.trim() === '') {
    errors.push('Formula cannot be empty');
    return { isValid: false, errors };
  }

  const trimmedFormula = formula.trim();

  // Check for balanced parentheses
  if (!hasBalancedParentheses(trimmedFormula)) {
    errors.push('Parentheses are not balanced');
  }

  // Extract all letter references from the formula
  const letterMatches = trimmedFormula.match(/\b[A-Z]\b/g) || [];
  const uniqueLetters = [...new Set(letterMatches)];

  // Check if all referenced letters exist in conditions
  const invalidLetters = uniqueLetters.filter(letter => !validLetters.includes(letter));
  if (invalidLetters.length > 0) {
    errors.push(`Invalid condition references: ${invalidLetters.join(', ')}`);
  }

  // Check if formula contains at least one letter reference
  if (uniqueLetters.length === 0) {
    errors.push('Formula must reference at least one condition');
  }

  // Check for valid operators (AND, OR, NOT)
  const operatorPattern = /\b(AND|OR|NOT)\b/g;
  const invalidOperators = trimmedFormula
    .replace(/\([^)]*\)/g, '') // Remove content in parentheses
    .replace(/\b[A-Z]\b/g, '') // Remove letter references
    .replace(operatorPattern, '') // Remove valid operators
    .replace(/\s+/g, '') // Remove whitespace
    .replace(/[()]/g, ''); // Remove parentheses

  if (invalidOperators.length > 0) {
    errors.push('Formula contains invalid characters or operators. Only AND, OR, NOT, parentheses, and condition letters are allowed');
  }

  // Check for consecutive operators
  if (/\b(AND|OR)\s+(AND|OR)\b/.test(trimmedFormula)) {
    errors.push('Formula cannot have consecutive AND/OR operators');
  }

  // Check if formula starts or ends with AND/OR
  if (/^\s*(AND|OR)\b/.test(trimmedFormula) || /\b(AND|OR)\s*$/.test(trimmedFormula)) {
    errors.push('Formula cannot start or end with AND/OR operators');
  }

  // Validate basic structure - ensure operators are between operands
  const tokens = tokenizeFormula(trimmedFormula);
  if (tokens.length > 0 && !validateTokenSequence(tokens)) {
    errors.push('Invalid formula structure. Operators must be between operands');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Checks if parentheses in the formula are balanced
 */
function hasBalancedParentheses(formula: string): boolean {
  let count = 0;
  for (const char of formula) {
    if (char === '(') count++;
    if (char === ')') count--;
    if (count < 0) return false; // Closing before opening
  }
  return count === 0;
}

/**
 * Tokenizes the formula into operands and operators
 */
function tokenizeFormula(formula: string): string[] {
  return formula
    .replace(/\(/g, ' ( ')
    .replace(/\)/g, ' ) ')
    .split(/\s+/)
    .filter(token => token.length > 0);
}

/**
 * Validates the sequence of tokens
 */
function validateTokenSequence(tokens: string[]): boolean {
  let expectOperand = true;
  let parenDepth = 0;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token === '(') {
      if (!expectOperand) return false;
      parenDepth++;
      continue;
    }

    if (token === ')') {
      if (expectOperand) return false;
      parenDepth--;
      if (parenDepth < 0) return false;
      continue;
    }

    if (token === 'NOT') {
      if (!expectOperand) return false;
      continue;
    }

    if (token === 'AND' || token === 'OR') {
      if (expectOperand) return false;
      expectOperand = true;
      continue;
    }

    // It's a letter (operand)
    if (/^[A-Z]$/.test(token)) {
      if (!expectOperand) return false;
      expectOperand = false;
      continue;
    }

    // Unknown token
    return false;
  }

  // Should end expecting operator (not operand) and balanced parens
  return !expectOperand && parenDepth === 0;
}

/**
 * Gets helpful example formulas
 */
export function getFormulaExamples(): string[] {
  return [
    'A AND B',
    'A OR B',
    '(A AND B) OR C',
    'A AND (B OR C)',
    'NOT A',
    '(A OR B) AND (C OR D)',
    'A AND B AND C',
  ];
}

